import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Download,
  FileDown,
  FileText,
  Loader2,
} from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { useToast } from 'components/ui/use-toast';
import { organizationAdmin } from 'services/api';
import type { OrganizationAdminScope } from 'services/api/organizationAdminClient';
import type {
  OrganizationAdminResponseAggregate,
  OrganizationAdminResponseComparisonCell,
  OrganizationAdminResponseQuestion,
  OrganizationAdminUserSummary,
} from 'types';

import { AggregateCards } from './aggregate-cards';
import { ComparisonTable, getComparisonCellKey } from './comparison-table';
import { OrganizationAdminHeader } from '../organization-admin-header';
import { useOrganizationAdminOverview } from '../use-organization-admin-overview';
import { QuestionPicker } from './question-picker';
import {
  buildExportFilename,
  getExportDate,
  getTypeLabel,
  triggerBlobDownload,
} from './reporting-utils';
import { UserPicker } from './user-picker';

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const candidate = error as {
    response?: { data?: { detail?: string } };
    message?: string;
  };
  return candidate.response?.data?.detail || candidate.message || fallback;
};

const EmptyReportState: React.FC = () => (
  <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
    <CardContent variant="custom" className="px-6 py-16 text-center">
      <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-foundation-orange-50">
        <FileText className="size-6 text-foundation-orange-500" />
      </div>
      <h2 className="mt-5 text-xl font-semibold text-foreground">Select questions to build responses</h2>
      <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
        The Responses tab shows aggregate results for all organization users. Select users only when you want a
        side-by-side comparison table.
      </p>
    </CardContent>
  </Card>
);

const ErrorPanel: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
    <CardContent variant="custom" className="px-6 py-12 text-center">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="size-5" />
      </div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </CardContent>
  </Card>
);

const ResponseDataPage: React.FC = () => {
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const targetOrganizationId = searchParams.get('organization_id')?.trim() || null;
  const organizationAdminScope = useMemo<OrganizationAdminScope | undefined>(
    () => (targetOrganizationId ? { organizationId: targetOrganizationId } : undefined),
    [targetOrganizationId],
  );
  const exportDate = getExportDate();
  const {
    overview,
    isLoading: overviewLoading,
    error: overviewError,
  } = useOrganizationAdminOverview(
    'Unable to load organization details right now.',
    organizationAdminScope,
  );
  const [questionSearch, setQuestionSearch] = useState('');
  const [questionCourseFilter, setQuestionCourseFilter] = useState('all');
  const [questionModuleFilter, setQuestionModuleFilter] = useState('all');
  const [questionTypeFilter, setQuestionTypeFilter] = useState('all');
  const [questionResults, setQuestionResults] = useState<OrganizationAdminResponseQuestion[]>([]);
  const [knownQuestions, setKnownQuestions] = useState<Record<number, OrganizationAdminResponseQuestion>>({});
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [showSelectedQuestionsOnly, setShowSelectedQuestionsOnly] = useState(false);
  const [showSelectedUsersOnly, setShowSelectedUsersOnly] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [aggregatesByQuestionId, setAggregatesByQuestionId] = useState<
    Record<number, OrganizationAdminResponseAggregate>
  >({});
  const [responseCellsByKey, setResponseCellsByKey] = useState<
    Record<string, OrganizationAdminResponseComparisonCell | null>
  >({});
  const [aggregateLoadingQuestionIds, setAggregateLoadingQuestionIds] = useState<number[]>([]);
  const [comparisonLoadingCellKeys, setComparisonLoadingCellKeys] = useState<string[]>([]);
  const [reportError, setReportError] = useState<string | null>(null);
  const [aggregateExportLoading, setAggregateExportLoading] = useState(false);
  const [usersExportLoading, setUsersExportLoading] = useState(false);

  useEffect(() => {
    setQuestionSearch('');
    setQuestionCourseFilter('all');
    setQuestionModuleFilter('all');
    setQuestionTypeFilter('all');
    setQuestionResults([]);
    setKnownQuestions({});
    setQuestionsLoading(false);
    setSelectedQuestionIds([]);
    setSelectedUserIds([]);
    setShowSelectedQuestionsOnly(false);
    setShowSelectedUsersOnly(false);
    setUserSearch('');
    setAggregatesByQuestionId({});
    setResponseCellsByKey({});
    setAggregateLoadingQuestionIds([]);
    setComparisonLoadingCellKeys([]);
    setReportError(null);
    setAggregateExportLoading(false);
    setUsersExportLoading(false);
  }, [targetOrganizationId]);

  useEffect(() => {
    let isMounted = true;
    const timer = window.setTimeout(async () => {
      try {
        setQuestionsLoading(true);
        const filters = {
          search: questionSearch,
          courseId: questionCourseFilter === 'all' ? undefined : Number(questionCourseFilter),
          moduleId: questionModuleFilter === 'all' ? undefined : Number(questionModuleFilter),
        };
        const results = organizationAdminScope
          ? await organizationAdmin.getResponseQuestions(filters, organizationAdminScope)
          : await organizationAdmin.getResponseQuestions(filters);
        if (!isMounted) {
          return;
        }
        setQuestionResults(results);
        setKnownQuestions((current) => {
          const next = { ...current };
          results.forEach((question) => {
            next[question.id] = question;
          });
          return next;
        });
      } catch (error) {
        console.error('Failed to load org admin response questions:', error);
        if (isMounted) {
          setQuestionResults([]);
        }
      } finally {
        if (isMounted) {
          setQuestionsLoading(false);
        }
      }
    }, 250);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [organizationAdminScope, questionCourseFilter, questionModuleFilter, questionSearch]);

  const organization = overview?.organization ?? null;
  const users = useMemo(() => overview?.users ?? [], [overview?.users]);
  const organizationName = organization?.name ?? 'Organization';

  const filteredUsers = useMemo(() => {
    const normalized = userSearch.trim().toLowerCase();
    if (!normalized) {
      return users;
    }
    return users.filter((user) => (
      user.display_name.toLowerCase().includes(normalized)
      || user.email.toLowerCase().includes(normalized)
    ));
  }, [userSearch, users]);

  const selectedQuestions = useMemo(
    () => selectedQuestionIds.map((id) => knownQuestions[id]).filter(Boolean),
    [knownQuestions, selectedQuestionIds],
  );

  const courseFilterOptions = useMemo(() => {
    const options = new Map<number, string>();
    Object.values(knownQuestions).forEach((question) => {
      question.courses?.forEach((course) => {
        options.set(course.id, course.name);
      });
    });
    return Array.from(options, ([id, name]) => ({ id, name }))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, [knownQuestions]);

  const moduleFilterOptions = useMemo(() => {
    const selectedCourseId = questionCourseFilter === 'all' ? null : Number(questionCourseFilter);
    const options = new Map<number, string>();
    Object.values(knownQuestions).forEach((question) => {
      if (
        selectedCourseId !== null
        && !(question.courses ?? []).some((course) => course.id === selectedCourseId)
      ) {
        return;
      }
      question.modules?.forEach((module) => {
        options.set(module.id, module.name);
      });
    });
    return Array.from(options, ([id, name]) => ({ id, name }))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, [knownQuestions, questionCourseFilter]);

  const questionTypeOptions = useMemo(() => {
    const options = new Map<string, string>();
    questionResults.forEach((question) => {
      options.set(question.normalized_type, getTypeLabel(question.normalized_type));
    });
    return Array.from(options, ([value, label]) => ({ value, label }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [questionResults]);

  useEffect(() => {
    if (questionTypeFilter === 'all') {
      return;
    }

    const hasActiveType = questionResults.some((question) => question.normalized_type === questionTypeFilter);
    if (!hasActiveType) {
      setQuestionTypeFilter('all');
    }
  }, [questionResults, questionTypeFilter]);

  const selectedUsers = useMemo(
    () => selectedUserIds
      .map((id) => users.find((user) => user.id === id))
      .filter(Boolean) as OrganizationAdminUserSummary[],
    [selectedUserIds, users],
  );

  const typeFilteredQuestions = useMemo(
    () => (questionTypeFilter === 'all'
      ? questionResults
      : questionResults.filter((question) => question.normalized_type === questionTypeFilter)),
    [questionResults, questionTypeFilter],
  );

  const displayedQuestions = useMemo(
    () => (showSelectedQuestionsOnly
      ? typeFilteredQuestions.filter((question) => selectedQuestionIds.includes(question.id))
      : typeFilteredQuestions),
    [selectedQuestionIds, showSelectedQuestionsOnly, typeFilteredQuestions],
  );

  const displayedUsers = useMemo(
    () => (showSelectedUsersOnly
      ? filteredUsers.filter((user) => selectedUserIds.includes(user.id))
      : filteredUsers),
    [filteredUsers, selectedUserIds, showSelectedUsersOnly],
  );

  const loadMissingReportData = async (questionIds: number[], userIds: number[] = []) => {
    const uniqueQuestionIds = Array.from(new Set(questionIds));
    const uniqueUserIds = Array.from(new Set(userIds));
    if (!uniqueQuestionIds.length) {
      return;
    }

    const aggregateQuestionIds = uniqueQuestionIds.filter(
      (questionId) => !aggregatesByQuestionId[questionId]
        && !aggregateLoadingQuestionIds.includes(questionId),
    );
    const comparisonCellKeys = uniqueUserIds.flatMap((userId) => (
      uniqueQuestionIds
        .map((questionId) => getComparisonCellKey(userId, questionId))
        .filter((cellKey) => responseCellsByKey[cellKey] === undefined
          && !comparisonLoadingCellKeys.includes(cellKey))
    ));
    const comparisonQuestionIds = uniqueQuestionIds.filter((questionId) => (
      uniqueUserIds.some((userId) => comparisonCellKeys.includes(getComparisonCellKey(userId, questionId)))
    ));
    const requestQuestionIds = Array.from(new Set([...aggregateQuestionIds, ...comparisonQuestionIds]));

    if (!requestQuestionIds.length) {
      return;
    }

    if (aggregateQuestionIds.length) {
      setAggregateLoadingQuestionIds((current) => Array.from(new Set([...current, ...aggregateQuestionIds])));
    }
    if (comparisonCellKeys.length) {
      setComparisonLoadingCellKeys((current) => Array.from(new Set([...current, ...comparisonCellKeys])));
    }
    setReportError(null);

    try {
      const payload = {
        question_ids: requestQuestionIds,
        user_ids: uniqueUserIds.length ? uniqueUserIds : undefined,
      };
      const data = organizationAdminScope
        ? await organizationAdmin.getResponseReport(payload, organizationAdminScope)
        : await organizationAdmin.getResponseReport(payload);
      setKnownQuestions((current) => {
        const next = { ...current };
        data.selected_questions.forEach((question) => {
          next[question.id] = question;
        });
        return next;
      });
      setAggregatesByQuestionId((current) => {
        const next = { ...current };
        data.aggregates.forEach((aggregate) => {
          next[aggregate.question_id] = aggregate;
        });
        return next;
      });
      if (uniqueUserIds.length) {
        const rowsByUserId = new Map(data.comparison_rows.map((row) => [row.user.id, row]));
        setResponseCellsByKey((current) => {
          const next = { ...current };
          uniqueUserIds.forEach((userId) => {
            const row = rowsByUserId.get(userId);
            requestQuestionIds.forEach((questionId) => {
              next[getComparisonCellKey(userId, questionId)] = row?.responses[String(questionId)] ?? null;
            });
          });
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to load org admin response data:', error);
      setReportError(getApiErrorMessage(error, 'Unable to load response data.'));
    } finally {
      if (aggregateQuestionIds.length) {
        setAggregateLoadingQuestionIds((current) => current.filter((id) => !aggregateQuestionIds.includes(id)));
      }
      if (comparisonCellKeys.length) {
        setComparisonLoadingCellKeys((current) => (
          current.filter((cellKey) => !comparisonCellKeys.includes(cellKey))
        ));
      }
    }
  };

  const toggleQuestion = (questionId: number) => {
    if (selectedQuestionIds.includes(questionId)) {
      setReportError(null);
      setSelectedQuestionIds((current) => current.filter((id) => id !== questionId));
      return;
    }
    setSelectedQuestionIds((current) => [...current, questionId]);
    void loadMissingReportData([questionId], selectedUserIds);
  };

  const toggleUser = (userId: number) => {
    if (selectedUserIds.includes(userId)) {
      setReportError(null);
      setSelectedUserIds((current) => current.filter((id) => id !== userId));
      return;
    }
    setSelectedUserIds((current) => [...current, userId]);
    void loadMissingReportData(selectedQuestionIds, [userId]);
  };

  const handleQuestionCourseFilterChange = (value: string) => {
    setQuestionCourseFilter(value);
    setQuestionModuleFilter('all');
  };

  const selectAllVisibleQuestions = () => {
    const visibleQuestionIds = displayedQuestions.map((question) => question.id);
    const addedQuestionIds = visibleQuestionIds.filter((questionId) => !selectedQuestionIds.includes(questionId));
    setSelectedQuestionIds((current) => Array.from(new Set([...current, ...visibleQuestionIds])));
    void loadMissingReportData(addedQuestionIds, selectedUserIds);
  };

  const deselectAllVisibleQuestions = () => {
    const visibleQuestionIds = new Set(displayedQuestions.map((question) => question.id));
    setReportError(null);
    setSelectedQuestionIds((current) => current.filter((questionId) => !visibleQuestionIds.has(questionId)));
  };

  const selectAllVisibleUsers = () => {
    const visibleUserIds = displayedUsers.map((user) => user.id);
    const addedUserIds = visibleUserIds.filter((userId) => !selectedUserIds.includes(userId));
    setSelectedUserIds((current) => Array.from(new Set([...current, ...visibleUserIds])));
    void loadMissingReportData(selectedQuestionIds, addedUserIds);
  };

  const deselectAllVisibleUsers = () => {
    const visibleUserIds = new Set(displayedUsers.map((user) => user.id));
    setReportError(null);
    setSelectedUserIds((current) => current.filter((userId) => !visibleUserIds.has(userId)));
  };

  const handleAggregateExport = async () => {
    if (!selectedQuestionIds.length) {
      return;
    }

    try {
      setAggregateExportLoading(true);
      const payload = {
        question_ids: selectedQuestionIds,
      };
      const response = organizationAdminScope
        ? await organizationAdmin.exportResponseAggregate(payload, organizationAdminScope)
        : await organizationAdmin.exportResponseAggregate(payload);
      triggerBlobDownload(
        response,
        `${buildExportFilename(organizationName, 'Response Data Aggregate', exportDate)}.csv`,
      );
      addToast({
        title: 'Export ready',
        description: 'Aggregate response data download has started.',
      });
    } catch (error) {
      console.error('Failed to export aggregate response data:', error);
      addToast({
        title: 'Export failed',
        description: getApiErrorMessage(error, 'Unable to export aggregate response data.'),
        variant: 'destructive',
      });
    } finally {
      setAggregateExportLoading(false);
    }
  };

  const handleUsersExport = async () => {
    if (!selectedQuestionIds.length) {
      return;
    }

    try {
      setUsersExportLoading(true);
      const payload = {
        question_ids: selectedQuestionIds,
        user_ids: selectedUserIds.length ? selectedUserIds : undefined,
      };
      const response = organizationAdminScope
        ? await organizationAdmin.exportResponseUsers(payload, organizationAdminScope)
        : await organizationAdmin.exportResponseUsers(payload);
      triggerBlobDownload(
        response,
        `${buildExportFilename(organizationName, 'Response Data Users', exportDate)}.csv`,
      );
      addToast({
        title: 'Export ready',
        description: selectedUserIds.length
          ? 'Selected user response data download has started.'
          : 'All-user response data download has started.',
      });
    } catch (error) {
      console.error('Failed to export user response data:', error);
      addToast({
        title: 'Export failed',
        description: getApiErrorMessage(error, 'Unable to export user response data.'),
        variant: 'destructive',
      });
    } finally {
      setUsersExportLoading(false);
    }
  };

  if (overviewLoading) {
    return (
      <main className="min-h-screen">
        <div className="flex min-h-[420px] items-center justify-center py-8">
          <LoadingAnimation />
        </div>
      </main>
    );
  }

  if (overviewError) {
    return (
      <main className="min-h-screen">
        <div className="py-8">
          <ErrorPanel title="Unable to load response data" message={overviewError} />
        </div>
      </main>
    );
  }

  if (!overview || !organization) {
    return (
      <main className="min-h-screen">
        <div className="py-8">
          <ErrorPanel
            title="No organization available"
            message="This page is only available when your account is attached to an organization."
          />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <div className="space-y-8 py-8">
        <OrganizationAdminHeader activeTab="response-data" organizationName={organizationName} />

        <section className="max-w-3xl">
          <h2 className="text-3xl font-semibold text-foreground">Responses</h2>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Review aggregate question responses and compare selected organization users. User response CSV exports
            selected users when present; otherwise it exports all organization users for the selected questions.
          </p>
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(22rem,0.8fr)]">
          <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
            <CardContent variant="custom" className="p-6">
              <QuestionPicker
                questions={displayedQuestions}
                selectedQuestionIds={selectedQuestionIds}
                search={questionSearch}
                courseFilter={questionCourseFilter}
                moduleFilter={questionModuleFilter}
                questionTypeFilter={questionTypeFilter}
                courseOptions={courseFilterOptions}
                moduleOptions={moduleFilterOptions}
                questionTypeOptions={questionTypeOptions}
                isLoading={questionsLoading}
                showSelectedOnly={showSelectedQuestionsOnly}
                onSearchChange={setQuestionSearch}
                onCourseFilterChange={handleQuestionCourseFilterChange}
                onModuleFilterChange={setQuestionModuleFilter}
                onQuestionTypeFilterChange={setQuestionTypeFilter}
                onShowSelectedOnlyChange={setShowSelectedQuestionsOnly}
                onToggleQuestion={toggleQuestion}
                onSelectAllQuestions={selectAllVisibleQuestions}
                onDeselectAllQuestions={deselectAllVisibleQuestions}
              />
            </CardContent>
          </Card>

          <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
            <CardContent variant="custom" className="p-6">
              <UserPicker
                users={displayedUsers}
                selectedUserIds={selectedUserIds}
                search={userSearch}
                showSelectedOnly={showSelectedUsersOnly}
                onSearchChange={setUserSearch}
                onShowSelectedOnlyChange={setShowSelectedUsersOnly}
                onToggleUser={toggleUser}
                onSelectAllUsers={selectAllVisibleUsers}
                onDeselectAllUsers={deselectAllVisibleUsers}
              />
            </CardContent>
          </Card>
        </section>

        {selectedQuestionIds.length === 0 ? <EmptyReportState /> : null}

        {selectedQuestionIds.length > 0 && reportError ? (
          <ErrorPanel title="Unable to load response data" message={reportError} />
        ) : null}

        {selectedQuestionIds.length > 0 ? (
          <>
            <AggregateCards
              questions={selectedQuestions}
              aggregatesByQuestionId={aggregatesByQuestionId}
              loadingQuestionIds={aggregateLoadingQuestionIds}
              exportAction={(
                <Button
                  type="button"
                  size="auto"
                  data-testid="org-admin-export-response-aggregate"
                  onClick={handleAggregateExport}
                  disabled={!selectedQuestionIds.length || aggregateExportLoading}
                >
                  {aggregateExportLoading ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
                  {aggregateExportLoading ? 'Preparing...' : 'Aggregate CSV'}
                </Button>
              )}
            />
            <ComparisonTable
              questions={selectedQuestions}
              users={selectedUsers}
              responseCellsByKey={responseCellsByKey}
              loadingCellKeys={comparisonLoadingCellKeys}
              exportAction={(
                <Button
                  type="button"
                  size="auto"
                  data-testid="org-admin-export-response-users"
                  onClick={handleUsersExport}
                  disabled={!selectedQuestionIds.length || usersExportLoading}
                >
                  {usersExportLoading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  {usersExportLoading ? 'Preparing...' : 'User Responses CSV'}
                </Button>
              )}
            />
          </>
        ) : null}
      </div>
    </main>
  );
};

export default ResponseDataPage;
